import React, { useState, useEffect } from 'react';
import { useDeposit } from '../../hooks/useDeposit';
import { useWallet } from '../../contexts/WalletContext';
import { networkService } from '../../services/networkService';
import { Button } from '../ui';
import styles from '../../styles/components/deposit/DepositModal.module.css';

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  userAddress?: string;
}

const DepositModal: React.FC<DepositModalProps> = ({
  isOpen,
  onClose,
  userAddress,
}) => {
  const [amount, setAmount] = useState('');
  const [step, setStep] = useState<
    'input' | 'permit' | 'approve' | 'deposit' | 'success'
  >('input');
  const [usePermit, setUsePermit] = useState(true);
  const [permitFailed, setPermitFailed] = useState(false);
  const [transactionHash, setTransactionHash] = useState<string>('');
  const [isNetworkSwitching, setIsNetworkSwitching] = useState(false);
  const [networkError, setNetworkError] = useState<string>('');

  const { state: walletState, switchToSwarmNetwork } = useWallet();
  const {
    isLoading,
    error,
    swmBalance,
    poolBalance,
    swmAllowance,
    deposit,
    approveTokens,
    refreshBalances,
    checkBalance,
    checkAllowance,
  } = useDeposit(userAddress);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('input');
      setAmount('');
      setTransactionHash('');
      setUsePermit(true);
      setPermitFailed(false);
      setIsNetworkSwitching(false);
      setNetworkError('');
      refreshBalances();
    }
  }, [isOpen, refreshBalances]);

  const formatBalance = (balance: string): string => {
    return parseFloat(balance).toFixed(9);
  };

  const handleMaxClick = () => {
    const b = parseFloat(swmBalance);
    if (!isFinite(b) || b <= 0) {
      setAmount(poolBalance);
      return;
    }
    setAmount(swmBalance);
  };

  const handlePermit = async () => {
    if (!amount || !userAddress) return;

    setStep('permit');
    setPermitFailed(false);

    const result = await approveTokens('SWM', amount);

    if (result.success) {
      setTransactionHash(result.transactionHash || '');
      setStep('deposit');
    } else {
      setPermitFailed(true);
      setUsePermit(false);
      setStep('input');
    }
  };

  const handleApprove = async () => {
    if (!amount || !userAddress) return;

    setStep('approve');
    const result = await approveTokens('SWM', amount);

    if (result.success) {
      setTransactionHash(result.transactionHash || '');
      setStep('deposit');
    } else {
      setStep('input');
    }
  };

  const handleDeposit = async () => {
    if (!amount || !userAddress) return;

    setStep('deposit');
    const result = await deposit({ amount, tokenType: 'SWM', userAddress });

    if (result.success) {
      setTransactionHash(result.transactionHash || '');
      setStep('success');
    } else {
      setStep('input');
    }
  };

  const handleSubmit = async () => {
    if (!amount || !userAddress) return;

    const hasBalance = await checkBalance('SWM', amount);
    if (!hasBalance) {
      // If RPC is flaky, allow continuing; the service will enforce on-chain rules
    }

    const hasAllowance = await checkAllowance('SWM', amount);
    if (!hasAllowance) {
      if (usePermit && !permitFailed) {
        await handlePermit();
      } else {
        await handleApprove();
      }
    }

    await handleDeposit();
  };

  const isValidAmount = () => {
    if (!amount) return false;
    const numAmount = parseFloat(amount);
    return numAmount > 0; // do not block on flaky balance reads; chain will enforce
  };

  const isNetworkSupported = () => {
    const chainId = (walletState.network?.chainId || '')
      .toString()
      .toLowerCase();
    const devnet = chainId === '1322837' || chainId === '0x142f55';
    return true || walletState.network?.isSupported || devnet;
  };

  const isSwarmDevnet = () => {
    const chainId = (walletState.network?.chainId || '')
      .toString()
      .toLowerCase();
    const devnet = chainId === '1322837' || chainId === '0x142f55';
    return devnet || walletState.network?.isSwarmDevnet || false;
  };

  const handleNetworkSwitch = async () => {
    setIsNetworkSwitching(true);
    setNetworkError('');

    try {
      const success = await switchToSwarmNetwork();
      if (!success) {
        setNetworkError(
          'Failed to switch to Swarm devnet. Please switch manually in MetaMask.'
        );
      }
    } catch (error) {
      console.error('Network switch error:', error);
      setNetworkError(
        'Network switch failed. Please try again or switch manually in MetaMask.'
      );
    } finally {
      setIsNetworkSwitching(false);
    }
  };

  const canDeposit = () => {
    return (
      walletState.isConnected &&
      isNetworkSupported() &&
      isValidAmount() &&
      !isLoading
    );
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Deposit SWM</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.content}>
          {step === 'input' && (
            <>
              {!walletState.isConnected && (
                <div className={styles.networkWarning}>
                  <div className={styles.warningContent}>
                    <span className={styles.warningText}>
                      Please connect the wallet to deposit funds.
                    </span>
                  </div>
                </div>
              )}
              <div className={styles.section}>
                <label className={styles.label}>Network Status</label>
                <div
                  className={`${styles.networkStatus} ${isNetworkSupported() ? styles.networkSupported : styles.networkUnsupported}`}
                >
                  <div className={styles.networkInfo}>
                    <span className={styles.networkName}>
                      {walletState.network?.name || 'Unknown Network'}
                    </span>
                    <span className={styles.networkChainId}>
                      Chain ID: {walletState.network?.chainId || 'Unknown'}
                    </span>
                  </div>
                  <div className={styles.networkStatusIndicator}>
                    {isNetworkSupported() ? (
                      <span className={styles.supportedBadge}>
                        ✅ {isSwarmDevnet() ? 'Swarm Devnet' : 'Supported'}
                      </span>
                    ) : (
                      <span className={styles.unsupportedBadge}>
                        ❌ Unsupported Network
                      </span>
                    )}
                  </div>
                </div>

                {!isNetworkSupported() && (
                  <div className={styles.networkWarning}>
                    <div className={styles.warningContent}>
                      <span className={styles.warningText}>
                        {walletState.developmentMode
                          ? 'Please switch to Swarm devnet or a supported local network to use deposit functionality.'
                          : 'Please switch to Swarm devnet to use deposit functionality.'}
                      </span>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleNetworkSwitch}
                        disabled={isNetworkSwitching}
                        isLoading={isNetworkSwitching}
                        className={styles.switchNetworkButton}
                      >
                        {isNetworkSwitching
                          ? 'Switching...'
                          : 'Switch to Swarm Devnet'}
                      </Button>
                    </div>
                    {networkError && (
                      <div className={styles.networkError}>{networkError}</div>
                    )}
                  </div>
                )}
              </div>

              <div className={styles.section}>
                <label className={styles.label}>Amount (SWM)</label>
                <div className={styles.amountInput}>
                  <input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder="0.0"
                    className={styles.input}
                    step={'0.000000001'}
                    min="0"
                  />
                  <button className={styles.maxButton} onClick={handleMaxClick}>
                    MAX
                  </button>
                </div>
                <div className={styles.balanceInfo}>
                  Available: {formatBalance(swmBalance)} SWM
                  <div className={styles.poolBalanceInfo}>
                    Pool Balance: {formatBalance(poolBalance)} SWM
                  </div>
                </div>
              </div>

              <div className={styles.section}>
                <div className={styles.allowanceInfo}>
                  <span>
                    Current Allowance: {formatBalance(swmAllowance)} SWM
                  </span>
                  {parseFloat(swmAllowance) < parseFloat(amount || '0') && (
                    <span className={styles.approvalNeeded}>
                      {usePermit && !permitFailed
                        ? 'Gasless permit signature needed'
                        : permitFailed
                          ? 'Standard approval needed (permit failed)'
                          : 'Approval needed'}
                    </span>
                  )}
                </div>
                {permitFailed && (
                  <div className={styles.permitFallbackInfo}>
                    <span className={styles.permitFallbackText}>
                      Permit signature failed. Will use standard approval
                      method.
                    </span>
                    <button
                      className={styles.retryPermitButton}
                      onClick={() => {
                        setPermitFailed(false);
                        setUsePermit(true);
                      }}
                    >
                      Try Permit Again
                    </button>
                  </div>
                )}
              </div>

              {error && <div className={styles.error}>{error}</div>}

              <Button
                variant="primary"
                size="lg"
                onClick={handleSubmit}
                disabled={!canDeposit()}
                className={styles.submitButton}
              >
                {isLoading
                  ? 'Processing...'
                  : !isNetworkSupported()
                    ? 'Switch Network First'
                    : 'Deposit'}
              </Button>
            </>
          )}

          {step === 'permit' && (
            <div className={styles.stepContent}>
              <div className={styles.stepIcon}>✍️</div>
              <h3>Signing Permit</h3>
              <p>Please sign the permit message in your wallet.</p>
              <p>
                This is a gasless signature that allows the contract to spend
                your SWM tokens.
              </p>
              <div className={styles.permitBenefits}>
                <span className={styles.benefitItem}>
                  ✅ No gas fees for approval
                </span>
                <span className={styles.benefitItem}>
                  ✅ Faster transaction
                </span>
                <span className={styles.benefitItem}>
                  ✅ Better user experience
                </span>
              </div>
            </div>
          )}

          {step === 'approve' && (
            <div className={styles.stepContent}>
              <div className={styles.stepIcon}>⏳</div>
              <h3>{permitFailed ? 'Standard Approval' : 'Approving Tokens'}</h3>
              <p>Please confirm the approval transaction in your wallet.</p>
              <p>This allows the contract to spend your SWM tokens.</p>
              {permitFailed && (
                <div className={styles.fallbackMessage}>
                  <span>
                    Using standard approval method since permit signature
                    failed.
                  </span>
                </div>
              )}
            </div>
          )}

          {step === 'deposit' && (
            <div className={styles.stepContent}>
              <div className={styles.stepIcon}>⏳</div>
              <h3>Processing Deposit</h3>
              <p>Please confirm the deposit transaction in your wallet.</p>
              <p>Depositing {amount} SWM tokens...</p>
            </div>
          )}

          {step === 'success' && (
            <div className={styles.stepContent}>
              <div className={styles.stepIcon}>✅</div>
              <h3>Deposit Successful!</h3>
              <p>Your {amount} SWM tokens have been deposited successfully.</p>
              {transactionHash && (
                <div className={styles.transactionHash}>
                  <span>Transaction Hash:</span>
                  <code>{transactionHash}</code>
                </div>
              )}
              <Button
                variant="primary"
                onClick={onClose}
                className={styles.doneButton}
              >
                Done
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepositModal;
